import {
  CURRENCY_CODES,
  isFractionUnit,
  magnitudeOf,
  NUMBER_IN_SLICE,
  readUnit,
  roundHalfUp,
  scaleAfter,
  scaleOfUnit,
  scaleSize,
  unitSuffix,
  writesWholeAmount
} from "./figure";

const grouped = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const writtenGrouped = (number: string): boolean =>
  number.includes(",") || number.replace(/^[+\-−]/, "").split(".")[0].length < 4;

export const exactly = (value: number): string => String(Number(value.toPrecision(12)));

const toFixedHalfUp = (value: number, dp: number): string =>
  Math.abs(value) * 10 ** dp < Number.MAX_SAFE_INTEGER ? roundHalfUp(value, dp).toFixed(dp) : value.toFixed(dp);

const spellAt = (value: number, floor: number): string => {
  for (let dp = floor; dp < 6; dp++) {
    const spelled = toFixedHalfUp(value, dp);
    const tolerance = Math.abs(value) * 0.005 || 0.5 * 10 ** -dp;
    if (Math.abs(Number(spelled) - value) <= tolerance) return spelled;
  }
  return toFixedHalfUp(value, 6);
};

const exactAt = (value: number, places: number): string => {
  const exact = exactly(value);
  return (exact.split(".")[1] ?? "").length >= places ? exact : toFixedHalfUp(value, places);
};

const CURRENCY = `(?:[A-Z]{1,3}\\$|[$€£¥]|(?:${CURRENCY_CODES.join("|")})[ \\u00a0])`;
const MINUS_BEFORE_CURRENCY = new RegExp(`([-−])(${CURRENCY})$`);
const CURRENCY_LAST = new RegExp(`${CURRENCY}$`);

export type TSlotReading = { unscaled?: boolean; exact?: boolean };

export const spellValue = (
  written: string,
  value: number,
  unit?: string | null,
  dp?: number | null,
  slot?: TSlotReading,
  exact = !!slot?.exact
): string => {
  if (Number.isNaN(value)) return written.replace(NUMBER_IN_SLICE, "—");
  if (!Number.isFinite(value)) return written;
  const accounting = /^\((.*)\)$/.exec(written.trim());
  if (accounting) {
    const inner = spellValue(accounting[1], Math.abs(value), unit, dp, slot && { unscaled: slot.unscaled }, exact);
    if (!slot) return `(${inner})`;
    return value < 0 ? `(${inner})` : inner;
  }
  const match = NUMBER_IN_SLICE.exec(written);
  if (!match) return written;
  const number = match[0];
  const before = written.slice(0, match.index);
  let after = written.slice(match.index + number.length);
  const minusFirst = MINUS_BEFORE_CURRENCY.exec(before);
  const writtenNegative = /^[-−]/.test(number) || !!minusFirst;
  let floor = (number.split(".")[1] ?? "").length;
  let writtenPlaces = floor;

  let magnitude = Math.abs(value);
  const scale = scaleAfter(after, unit);
  let held = typeof dp === "number" && dp >= 0 ? dp : undefined;
  let factor = 1;
  if (unit !== undefined) {
    const unitScale = scaleOfUnit(unit);
    if (scale) factor = unitScale / scaleSize(scale.ladder, scale.step);
    else if (isFractionUnit(unit) && /^[ \u00a0]?%/.test(after)) factor = 100;
    else if (slot ? slot.unscaled : writesWholeAmount(written, magnitudeOf(number), value, unitScale))
      factor = unitScale;
  }
  magnitude *= factor;
  if (held !== undefined && factor !== 1) held = Math.max(0, held - Math.round(Math.log10(factor)));
  if (held !== undefined) floor = Math.min(floor, held);
  if (scale && magnitude > 0) {
    const { base, words } = scale.ladder;
    const places = Math.round(Math.log10(base));
    let step = scale.step;
    while (magnitude >= base && step < words.length - 1) {
      magnitude /= base;
      step += 1;
    }
    while (magnitude < 1 && step > 0) {
      magnitude *= base;
      step -= 1;
      floor = Math.max(0, floor - places);
      writtenPlaces = Math.max(0, writtenPlaces - places);
    }
    if (step !== scale.step) {
      const gap = words[step] === "k" ? "" : scale.gap;
      after = `${gap}${words[step]}${after.slice(scale.gap.length + scale.word.length)}`;
    }
  }

  const [whole, decimals] = (exact ? exactAt(magnitude, writtenPlaces) : spellAt(magnitude, floor)).split(".");
  const digits = `${writtenGrouped(number) ? grouped(whole) : whole}${decimals ? `.${decimals}` : ""}`;
  const valueNegative = value < 0;
  const negative = value === 0 ? false : slot ? valueNegative : writtenNegative;
  const minus = (minusFirst?.[1] ?? number[0]) === "−" ? "−" : "-";
  if (minusFirst)
    return `${before.slice(0, minusFirst.index)}${negative ? minus : ""}${minusFirst[2]}${digits}${after}`;
  const currency = negative && !writtenNegative ? CURRENCY_LAST.exec(before) : null;
  if (currency) return `${before.slice(0, currency.index)}${minus}${currency[0]}${digits}${after}`;
  const sign = negative ? minus : number.startsWith("+") ? "+" : "";
  return `${before}${sign}${digits}${after}`;
};

const CURRENCY_SIGNS: Record<string, string> = {
  $: "USD",
  US$: "USD",
  "€": "EUR",
  "£": "GBP",
  "¥": "JPY",
  A$: "AUD",
  C$: "CAD",
  HK$: "HKD",
  NZ$: "NZD",
  S$: "SGD"
};
const SIGN_OF: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  AUD: "A$",
  CAD: "C$",
  HKD: "HK$",
  NZD: "NZ$",
  SGD: "S$"
};
const LATIN_NAMES = ["thousands", "millions", "billions", "trillions"];
const SCALE_SPELLINGS: { names: string[]; spellings: (string | null)[] }[] = [
  { names: LATIN_NAMES, spellings: ["K", "M", "B", "T"] },
  { names: LATIN_NAMES, spellings: ["k", "m", "bn", "tn"] },
  { names: LATIN_NAMES, spellings: ["k", "mn", "bn", "tn"] },
  { names: LATIN_NAMES, spellings: [null, " mn", " bn", " tn"] },
  { names: LATIN_NAMES, spellings: [null, "\u00a0mn", "\u00a0bn", "\u00a0tn"] },
  { names: LATIN_NAMES, spellings: [" thousand", " million", " billion", " trillion"] },
  { names: LATIN_NAMES, spellings: ["\u00a0thousand", "\u00a0million", "\u00a0billion", "\u00a0trillion"] },
  { names: ["ten-thousands", "hundred-millions", "trillions"], spellings: ["万", "億", "兆"] }
];
const USUAL_SPELLING: Record<string, string> = {
  thousands: "K",
  millions: "M",
  billions: "B",
  trillions: "T",
  "ten-thousands": "万",
  "hundred-millions": "億"
};
const CJK_SCALE = /^[万億兆]/;

const scaleNameOf = (word: string): string | undefined => {
  for (const { names, spellings } of SCALE_SPELLINGS) {
    const step = spellings.findIndex((spelling) => spelling !== null && spelling.trim() === word);
    if (step >= 0) return names[step];
  }
  return undefined;
};

export type TSlotFormat = TSlotReading & {
  dp: number;
  grouped: boolean;
  negative: boolean;
  currency?: string;
  sign?: string;
  scale?: string;
  scaleSpelling?: string;
  unit?: string;
  at?: number;
};

export type TFillRef = { key: string; start?: number; len?: number; format?: TSlotFormat };

const ISO_BEFORE = new RegExp(`^(${CURRENCY_CODES.join("|")})[ \\u00a0]$`);

const formatOf = (
  number: string,
  before: string,
  after: string,
  at?: number,
  value?: number,
  unit?: string | null
): TSlotFormat => {
  const minusFirst = MINUS_BEFORE_CURRENCY.exec(before);
  const currencySign = minusFirst && minusFirst.index === 0 ? minusFirst[2] : before;
  const currency = currencySign ? CURRENCY_SIGNS[currencySign] ?? ISO_BEFORE.exec(currencySign)?.[1] : undefined;
  const sign = before && (!currency || SIGN_OF[currency] !== before) ? before : undefined;
  let scale: string | undefined;
  let scaleSpelling: string | undefined;
  let rest = after;
  const own = unit !== undefined && unitSuffix(after, unit);
  for (const { names, spellings } of own ? [] : SCALE_SPELLINGS) {
    const step = spellings.findIndex(
      (spelling) =>
        spelling !== null &&
        after.startsWith(spelling) &&
        (CJK_SCALE.test(spelling) || !/^[\p{L}\p{N}]/u.test(after.slice(spelling.length)))
    );
    if (step < 0) continue;
    const spelled = spellings[step] as string;
    scale = names[step];
    if (spelled !== USUAL_SPELLING[scale]) scaleSpelling = spelled;
    rest = after.slice(spelled.length);
    break;
  }
  const accounting = before.startsWith("(") && after.endsWith(")");
  const negative = /^[-−]/.test(number) || !!minusFirst || accounting;
  const known = value !== undefined && Number.isFinite(value) ? value : undefined;
  const unscaled =
    !scale &&
    unit !== undefined &&
    writesWholeAmount(`${before}${number}${after}`, magnitudeOf(number), known, scaleOfUnit(unit));
  return {
    dp: (number.split(".")[1] ?? "").length,
    grouped: writtenGrouped(number),
    negative,
    ...(currency ? { currency } : {}),
    ...(sign ? { sign } : {}),
    ...(scale ? { scale } : {}),
    ...(scaleSpelling ? { scaleSpelling } : {}),
    ...(rest ? { unit: rest } : {}),
    ...(at === undefined ? {} : { at }),
    ...(unscaled ? { unscaled: true } : {})
  };
};

const spelledAround = (format: TSlotFormat): { before: string; after: string } => {
  const before = format.sign ?? (format.currency ? SIGN_OF[format.currency] ?? "" : "");
  const scale = format.scale ? format.scaleSpelling ?? USUAL_SPELLING[format.scale] ?? "" : "";
  return { before, after: `${scale}${format.unit ?? ""}` };
};

const sampleOf = (format: TSlotFormat) => {
  const { before, after } = spelledAround(format);
  const minus = format.negative && !/^[-−(]/.test(before) ? "-" : "";
  return `${before}${minus}${format.grouped ? "1,000" : "1000"}${
    format.dp > 0 ? `.${"0".repeat(format.dp)}` : ""
  }${after}`;
};

const CURRENCY_BEFORE = new RegExp(`[-−]?${CURRENCY}$`);
const FORMAT_AFTER =
  /^(?:%|(?:K|M|B|T|k|m|mn|bn|tn)(?![\p{L}\p{N}_])|[ \u00a0](?:mn|bn|tn|thousand|million|billion|trillion)(?![\p{L}\p{N}_])|[万億兆])/u;

const figureWindow = (text: string, start: number, end: number, floor = 0, unit?: string | null) => {
  const before = CURRENCY_BEFORE.exec(text.slice(floor, start))?.[0] ?? "";
  const own = unitSuffix(text.slice(end), unit);
  const scale = own ? "" : FORMAT_AFTER.exec(text.slice(end))?.[0] ?? "";
  const after = own || scale + unitSuffix(text.slice(end + scale.length), unit);
  return { from: start - before.length, to: end + after.length };
};

const spanWindow = (text: string, start: number, end: number, floor: number, unit?: string | null) => {
  const number = NUMBER_IN_SLICE.exec(text.slice(start, end));
  if (!number) return null;
  const numberStart = start + number.index;
  const numberEnd = numberStart + number[0].length;
  const window = figureWindow(text, numberStart, numberEnd, floor, unit);
  return {
    from: Math.min(window.from, start),
    to: Math.max(window.to, end),
    numberStart,
    numberEnd,
    number: number[0]
  };
};

export const writtenAt = (text: string, start: number, len: number, unit?: string | null): string => {
  const window = spanWindow(text, start, start + len, 0, unit);
  return window ? text.slice(window.from, window.to) : text.slice(start, start + len);
};

export type TStripValueOf = (key: string, written: string) => number | null | undefined;

export type TDashFormat = (key: string) => TSlotFormat | undefined;

const DASH = "—";

export const holdsDash = (written: string): boolean => written.includes(DASH) && !/\d/.test(written);

const withoutPlace = (format: TSlotFormat): TSlotFormat => {
  const { at: _at, ...rest } = format;
  return rest;
};

export type TKeptFormat = (key: string, written: string) => TSlotFormat | undefined;

type TWindow = { from: number; to: number; delta: number };

const shiftedPast = <R extends TFillRef>(ref: R, windows: TWindow[]): R | null => {
  if (ref.start === undefined || ref.len === undefined) return ref;
  const start = ref.start;
  const end = start + ref.len;
  if (windows.some((window) => start < window.to && window.from < end)) return null;
  const delta = windows.filter((window) => window.to <= start).reduce((sum, window) => sum + window.delta, 0);
  return delta === 0 ? ref : { ...ref, start: start + delta };
};

export const stripText = <R extends TFillRef>(
  text: string,
  refs: R[],
  holds: (key: string) => boolean,
  unitOf?: (key: string) => string | null | undefined,
  valueOf?: TStripValueOf,
  dashFormat?: TDashFormat,
  keptFormat?: TKeptFormat
): { text: string; refs: R[] } => {
  const dashed = (ref: R) =>
    dashFormat && text.slice(ref.start, (ref.start as number) + (ref.len as number)) === DASH
      ? dashFormat(ref.key)
      : undefined;
  const spans = refs
    .map((ref, index) => ({ ref, index }))
    .filter(({ ref }) => ref.start !== undefined && ref.len !== undefined && ref.len > 0)
    .map(({ ref, index }) => ({ ref, index, dash: dashed(ref) }))
    .filter(({ ref, dash }) => dash || holds(ref.key))
    .sort((a, b) => (a.ref.start as number) - (b.ref.start as number));
  const moved: (R | null)[] = [...refs];
  const slots = new Set<number>();
  const removed: TWindow[] = [];
  let out = "";
  let last = 0;
  for (const { ref, index, dash } of spans) {
    const start = ref.start as number;
    const end = start + (ref.len as number);
    if (start < last || end > text.length) continue;
    if (dash) {
      out += text.slice(last, start);
      moved[index] = { ...ref, start: out.length, len: 0, format: withoutPlace(dash) };
      slots.add(index);
      removed.push({ from: start, to: end, delta: start - end });
      last = end;
      continue;
    }
    const unit = unitOf?.(ref.key);
    const window = spanWindow(text, start, end, last, unit);
    if (!window) continue;
    out += text.slice(last, window.from);
    const written = text.slice(window.from, window.to);
    const kept = keptFormat?.(ref.key, written);
    const format = kept
      ? withoutPlace(kept)
      : formatOf(
          window.number,
          text.slice(window.from, window.numberStart),
          text.slice(window.numberEnd, window.to),
          undefined,
          valueOf?.(ref.key, written) ?? undefined,
          unit
        );
    moved[index] = { ...ref, start: out.length, len: 0, format };
    slots.add(index);
    removed.push({ from: window.from, to: window.to, delta: window.from - window.to });
    last = window.to;
  }
  return {
    text: out + text.slice(last),
    refs: moved
      .map((ref, index) => (slots.has(index) || !ref ? ref : shiftedPast(ref, removed)))
      .filter((ref): ref is R => ref !== null)
  };
};

export const stripValue = (
  written: string,
  unit?: string | null,
  value?: number | null
): { written: string; format: TSlotFormat; figure: string } | null => {
  const number = NUMBER_IN_SLICE.exec(written);
  if (!number) return null;
  const window = figureWindow(written, number.index, number.index + number[0].length, 0, unit);
  const enclosed =
    written[window.from - 1] === "(" &&
    written[window.to] === ")" &&
    /^[\s~≈]*$/.test(written.slice(0, window.from - 1));
  const from = enclosed ? window.from - 1 : window.from;
  const to = enclosed ? window.to + 1 : window.to;
  return {
    written: written.slice(0, from) + written.slice(to),
    figure: written.slice(from, to),
    format: formatOf(
      number[0],
      written.slice(from, number.index),
      written.slice(number.index + number[0].length, to),
      from,
      value ?? undefined,
      unit
    )
  };
};

const standIn = (format: TSlotFormat, hasValue: boolean) => (hasValue ? sampleOf(format) : DASH);

const spelledAt = (
  written: string,
  format: TSlotFormat & { at: number },
  value: number | undefined,
  unit?: string | null,
  dp?: number | null
) =>
  written.slice(0, format.at) +
  (value === undefined ? DASH : spellValue(sampleOf(format), value, unit, dp, format)) +
  written.slice(format.at);

const placed = (format: TSlotFormat | undefined): format is TSlotFormat & { at: number } => format?.at !== undefined;

const materialize = <R extends TFillRef>(
  text: string,
  refs: R[],
  hasValue: (key: string) => boolean,
  keepsSlot: (key: string) => boolean
): { text: string; refs: R[] } => {
  const slots = refs
    .map((ref, index) => ({ ref, index }))
    .filter(({ ref }) => ref.len === 0 && ref.start !== undefined && ref.format && !keepsSlot(ref.key))
    .sort((a, b) => (b.ref.start as number) - (a.ref.start as number) || b.index - a.index);
  if (slots.length === 0) return { text, refs };
  let out = text;
  const moved = [...refs];
  for (const { ref, index } of slots) {
    const start = ref.start as number;
    const sample = standIn(ref.format as TSlotFormat, hasValue(ref.key));
    out = out.slice(0, start) + sample + out.slice(start);
    moved[index] = { ...ref, len: sample.length };
    for (let other = 0; other < moved.length; other++) {
      const o = moved[other];
      if (other === index || o.start === undefined) continue;
      if (o.start > start || (o.start === start && !(o.len === 0 && other < index)))
        moved[other] = { ...o, start: o.start + sample.length };
    }
  }
  return { text: out, refs: moved };
};

const NO_SLOT_KEPT = () => false;

const isSlot = (ref: TFillRef) => ref.len === 0 && ref.format !== undefined;

export const fillText = <R extends TFillRef>(
  storedText: string,
  storedRefs: R[],
  valueOf: (key: string) => number | null | undefined,
  unitOf?: (key: string) => string | null | undefined,
  dpOf?: (key: string) => number | null | undefined,
  keepsSlot: (key: string) => boolean = NO_SLOT_KEPT,
  setByReader: (key: string) => boolean = NO_SLOT_KEPT
): { text: string; refs: R[] } => {
  const has = (key: string) => {
    const value = valueOf(key);
    return value !== null && value !== undefined;
  };
  const { text, refs } = materialize(storedText, storedRefs, has, keepsSlot);
  const spans = refs
    .map((ref, index) => ({ ref, index }))
    .filter(({ ref }) => ref.start !== undefined && ref.len !== undefined)
    .sort((a, b) => (a.ref.start as number) - (b.ref.start as number));
  const moved = refs.map((ref) => {
    if (isSlot(ref) && keepsSlot(ref.key)) return ref;
    const { format: _format, ...rest } = ref;
    return rest as R;
  });
  const placed = new Set<number>();
  const rewritten: TWindow[] = [];
  let out = "";
  let last = 0;
  for (const { ref, index } of spans) {
    const start = ref.start as number;
    const end = start + (ref.len as number);
    if (start < last || end > text.length) continue;
    placed.add(index);
    if (isSlot(ref) && keepsSlot(ref.key)) {
      out += text.slice(last, start);
      moved[index] = { ...ref, start: out.length };
      last = start;
      continue;
    }
    const value = valueOf(ref.key);
    const unit = unitOf?.(ref.key);
    const window = value === null || value === undefined ? null : spanWindow(text, start, end, last, unit);
    if (value === null || value === undefined || !window) {
      out += text.slice(last, start);
      moved[index] = { ...moved[index], start: out.length };
      out += text.slice(start, end);
      last = end;
      continue;
    }
    const filled = spellValue(
      text.slice(window.from, window.to),
      value,
      unit,
      dpOf?.(ref.key),
      ref.format,
      setByReader(ref.key)
    );
    out += text.slice(last, window.from);
    moved[index] = { ...moved[index], start: out.length, len: filled.length };
    rewritten.push({ from: window.from, to: window.to, delta: filled.length - (window.to - window.from) });
    out += filled;
    last = window.to;
  }
  return {
    text: out + text.slice(last),
    refs: moved
      .map((ref, index) => (placed.has(index) ? ref : shiftedPast(ref, rewritten)))
      .filter((ref): ref is R => ref !== null)
  };
};

type TFillBlock = {
  kind: string;
  text?: string;
  refs?: { key: string; start?: number; len?: number; row?: number; col?: number; format?: TSlotFormat }[];
  rows?: string[][];
  items?: { key?: string; value?: string; format?: TSlotFormat }[];
};

const filledFormat = (format: TSlotFormat | undefined) => (format ? { format: withoutPlace(format) } : {});

const isInCell = (ref: { start?: number; len?: number }) => ref.start !== undefined && ref.len !== undefined;

export const fillBlock = <B extends TFillBlock>(
  block: B,
  valueOf: (key: string) => number | null | undefined,
  readsAsNumber: (cell: string) => boolean,
  unitOf?: (key: string) => string | null | undefined,
  dpOf?: (key: string) => number | null | undefined,
  keepsSlot: (key: string) => boolean = NO_SLOT_KEPT,
  setByReader: (key: string) => boolean = NO_SLOT_KEPT
): B => {
  const known = (key: string | undefined) => {
    const value = key === undefined ? undefined : valueOf(key);
    return value === null || value === undefined ? undefined : value;
  };
  const kept = (key: string | undefined, format: TSlotFormat | undefined) =>
    key !== undefined && format?.at !== undefined && keepsSlot(key);
  if ((block.kind === "prose" || block.kind === "pullQuote") && typeof block.text === "string" && block.refs?.length) {
    const filled = fillText(block.text, block.refs, valueOf, unitOf, dpOf, keepsSlot, setByReader);
    return { ...block, text: filled.text, refs: filled.refs };
  }
  if (block.kind === "table" && block.rows && block.refs?.length) {
    const spans = new Map<string, { start: number; len: number }>();
    const rows = block.rows.map((row, rowIndex) =>
      row.map((stored, colIndex) => {
        const ref = block.refs?.find((candidate) => candidate.row === rowIndex && candidate.col === colIndex);
        if (!ref || kept(ref.key, ref.format)) return stored;
        const value = known(ref.key);
        const unit = unitOf?.(ref.key);
        const exact = { ...ref.format, ...(setByReader(ref.key) ? { exact: true } : {}) } as TSlotFormat;
        if (placed(ref.format)) {
          const cell = spelledAt(stored, { ...exact, at: ref.format.at }, value, unit, dpOf?.(ref.key));
          if (!readsAsNumber(cell))
            spans.set(`${rowIndex}:${colIndex}`, { start: ref.format.at, len: cell.length - stored.length });
          return cell;
        }
        if (isInCell(ref)) {
          const filled = fillText(stored, [ref], valueOf, unitOf, dpOf, NO_SLOT_KEPT, setByReader);
          const span = filled.refs[0];
          if (span && isInCell(span))
            spans.set(`${rowIndex}:${colIndex}`, { start: span.start as number, len: span.len as number });
          return filled.text;
        }
        return value === undefined || !readsAsNumber(stored)
          ? stored
          : spellValue(stored, value, unit, dpOf?.(ref.key), ref.format, setByReader(ref.key));
      })
    );
    const refs = block.refs.map((ref) => {
      if (kept(ref.key, ref.format)) return ref;
      const { format: _format, start: _start, len: _len, ...rest } = ref;
      const span = spans.get(`${ref.row}:${ref.col}`);
      return span ? { ...rest, ...span } : rest;
    });
    return { ...block, rows, refs };
  }
  if (block.kind === "headline" && block.items) {
    const items = block.items.map(({ format, ...item }) => {
      if (kept(item.key, format)) return { ...item, format };
      const value = known(item.key);
      const exact = !!item.key && setByReader(item.key);
      if (item.key && placed(format)) {
        const spelled = spelledAt(item.value ?? "", { ...format, exact }, value, unitOf?.(item.key), dpOf?.(item.key));
        return { ...item, value: spelled, ...filledFormat(format) };
      }
      return value === undefined || !item.value || !item.key
        ? { ...item, ...filledFormat(format) }
        : {
            ...item,
            value: spellValue(item.value, value, unitOf?.(item.key), dpOf?.(item.key), format, exact),
            ...filledFormat(format)
          };
    });
    return { ...block, items };
  }
  return block;
};

export const stripBlock = <B extends TFillBlock>(
  block: B,
  holds: (key: string) => boolean,
  readsAsNumber: (cell: string) => boolean,
  unitOf?: (key: string) => string | null | undefined,
  valueOf?: TStripValueOf,
  dashFormat?: TDashFormat,
  keptFormat?: TKeptFormat
): B => {
  if ((block.kind === "prose" || block.kind === "pullQuote") && typeof block.text === "string" && block.refs?.length) {
    const stripped = stripText(block.text, block.refs, holds, unitOf, valueOf, dashFormat, keptFormat);
    return { ...block, text: stripped.text, refs: stripped.refs };
  }
  if (block.kind === "table" && block.rows && block.refs?.length) {
    const formats = new Map<string, TSlotFormat>();
    const rows = block.rows.map((row, rowIndex) =>
      row.map((cell, colIndex) => {
        const ref = block.refs?.find((candidate) => candidate.row === rowIndex && candidate.col === colIndex);
        if (!ref || ref.format) return cell;
        if (isInCell(ref)) {
          const stripped = stripText(cell, [ref], holds, unitOf, valueOf, dashFormat, keptFormat);
          const slot = stripped.refs[0];
          if (!slot?.format || slot.len !== 0) return cell;
          formats.set(`${rowIndex}:${colIndex}`, { ...slot.format, at: slot.start as number });
          return stripped.text;
        }
        const dash = dashFormat && holdsDash(cell) ? dashFormat(ref.key) : undefined;
        if (dash) {
          const at = cell.indexOf(DASH);
          formats.set(`${rowIndex}:${colIndex}`, { ...dash, at });
          return cell.slice(0, at) + cell.slice(at + DASH.length);
        }
        if (!holds(ref.key) || !readsAsNumber(cell)) return cell;
        const stripped = stripValue(cell, unitOf?.(ref.key), valueOf?.(ref.key, cell));
        if (!stripped) return cell;
        const kept = keptFormat?.(ref.key, stripped.figure);
        formats.set(`${rowIndex}:${colIndex}`, kept ? { ...kept, at: stripped.format.at } : stripped.format);
        return stripped.written;
      })
    );
    const refs = block.refs.map((ref) => {
      const format = formats.get(`${ref.row}:${ref.col}`);
      if (!format) return ref;
      const { start: _start, len: _len, ...rest } = ref;
      return { ...rest, format };
    });
    return { ...block, rows, refs };
  }
  if (block.kind === "headline" && block.items) {
    const items = block.items.map((item) => {
      if (!item.key || !item.value || item.format?.at !== undefined) return item;
      const dash = dashFormat && holdsDash(item.value) ? item.format ?? dashFormat(item.key) : undefined;
      if (dash) {
        const at = item.value.indexOf(DASH);
        return {
          ...item,
          value: item.value.slice(0, at) + item.value.slice(at + DASH.length),
          format: { ...dash, at }
        };
      }
      if (!holds(item.key)) return item;
      const stripped = stripValue(item.value, unitOf?.(item.key), valueOf?.(item.key, item.value));
      if (!stripped) return item;
      const kept = keptFormat?.(item.key, stripped.figure);
      return { ...item, value: stripped.written, format: kept ? { ...kept, at: stripped.format.at } : stripped.format };
    });
    return { ...block, items };
  }
  return block;
};

export const spellSlot = (format: TSlotFormat, value: number, unit?: string | null, dp?: number | null): string =>
  Number.isFinite(value) ? spellValue(sampleOf(format), value, unit, dp, format) : "—";

// A figure written the way the run wrote it, as toast prints a tile from its literal: the places the
// value has, never more than the ledger's dp, so 17000 reads 17,000 and not 17,000.00.
export const spellLedgerValue = (value: number, unit?: string | null, dp?: number | null): string => {
  const own = (exactly(value).split(".")[1] ?? "").length;
  const places = typeof dp === "number" && dp >= 0 ? Math.min(own, dp) : own;
  return spellSlot(figureFormat(places, unit), value, unit, places);
};

const SYMBOL_UNIT = /^[%‰x×°]$/;
const ATTACHED_UNIT = /^[%‰x×°](?![\p{L}\p{N}])/u;

export const figureFormat = (dp: number | null | undefined, unit: string | null | undefined): TSlotFormat => {
  const base = { dp: typeof dp === "number" && dp >= 0 ? dp : 0, grouped: true, negative: false };
  const read = readUnit(unit);
  if (!read.currency) {
    const text = (unit ?? "").trim();
    if (!text) return base;
    return { ...base, unit: ATTACHED_UNIT.test(text) ? text : ` ${text}` };
  }
  const iso = /^[A-Z]{3}$/.test(read.currency);
  const currency = iso ? read.currency : CURRENCY_SIGNS[read.currency];
  const usual = currency ? SIGN_OF[currency] : undefined;
  const sign = iso ? (usual ? undefined : `${read.currency} `) : read.currency === usual ? undefined : read.currency;
  const scaleName = read.scale ? scaleNameOf(read.scale.word) : undefined;
  const rest = read.suffix.trim();
  return {
    ...base,
    ...(currency ? { currency } : {}),
    ...(sign ? { sign } : {}),
    ...(scaleName ? { scale: scaleName } : {}),
    ...(scaleName && read.scale && read.scale.word !== USUAL_SPELLING[scaleName]
      ? { scaleSpelling: /^[a-z]{4,}$/.test(read.scale.word) ? ` ${read.scale.word}` : read.scale.word }
      : {}),
    ...(rest ? { unit: rest.startsWith("/") ? rest : ` ${rest}` } : {})
  };
};

export const respellMark = (
  written: string,
  mark: { start: number; len: number },
  value: number | null | undefined,
  unit?: string | null,
  dp?: number | null,
  writtenFor?: number | null
): string => {
  const stripped = stripText<TFillRef>(
    written,
    [{ key: "", start: mark.start, len: mark.len }],
    () => true,
    () => unit,
    () => writtenFor ?? value,
    () => figureFormat(dp, unit)
  );
  const slot = stripped.refs[0];
  if (!slot?.format || slot.len !== 0) return written;
  return fillText(
    stripped.text,
    stripped.refs,
    () => value,
    () => unit,
    () => dp
  ).text;
};

export const respellTile = (
  written: string,
  value: number | null | undefined,
  unit?: string | null,
  dp?: number | null,
  writtenFor?: number | null
): string => {
  const known = value === null || value === undefined || !Number.isFinite(value) ? undefined : value;
  if (holdsDash(written)) {
    if (known === undefined) return written;
    const at = written.indexOf(DASH);
    return written.slice(0, at) + spellLedgerValue(known, unit, dp) + written.slice(at + DASH.length);
  }
  const stripped = stripValue(written, unit, writtenFor ?? known);
  if (!stripped) return written;
  return spelledAt(stripped.written, { ...stripped.format, at: stripped.format.at ?? 0 }, known, unit, dp);
};

export const listingSpelling = (value: number, unit: string | null, dp?: number | null): string => {
  const places = typeof dp === "number" && dp >= 0 ? dp : undefined;
  const [whole, decimals] = (places === undefined ? exactly(Math.abs(value)) : Math.abs(value).toFixed(places)).split(
    "."
  );
  const digits = `${value < 0 ? "-" : ""}${grouped(whole)}${decimals ? `.${decimals}` : ""}`;
  if (!unit) return digits;
  return SYMBOL_UNIT.test(unit) ? `${digits}${unit}` : `${digits} ${unit}`;
};

export const withTileUnit = (value: string, unit?: string | null): string =>
  !unit || value.trimEnd().endsWith(unit) ? value : `${value}${SYMBOL_UNIT.test(unit) ? "" : " "}${unit}`;
